<script setup lang="ts">
import Header from "@/components/organisms/common/Header.vue";
import AppFooter from "@/components/organisms/common/AppFooter.vue";
import { accountService } from "@/app/services/AccountService";
import { onBeforeMount } from "vue";
import { ref } from "vue";
import { WorkerMuteOffice } from "@/types/worker/WorkerMuteOffice";
import OfficeCard from "@/components/organisms/office/OfficeCard.vue";
import OfficeMuteButton from "@/components/organisms/common/OfficeMuteButton.vue";
import OfficeMuteModal from "@/components/organisms/common/OfficeMuteModal.vue";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { useRouter } from "vue-router";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { useToast } from "@/utils/toast";
import { computed } from "vue";
import { orderStore } from "@/store/order";

const loader = useLoader();
const errorModal = useErrorModal();
const router = useRouter();
const toast = useToast();

const mutedOffices = ref<WorkerMuteOffice[]>([]);
const isShownMutedOfficeModal = ref(false);
const selectedOfficeIndex = ref<number | null>(null);
const initialized = ref(false);

onBeforeMount(async () => {
  loader.show();
  try {
    await fetchMutedOffices();
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    initialized.value = true;
    loader.hide();
  }
});

const selectedOfficeName = computed(() => {
  if (selectedOfficeIndex.value === null) {
    return "";
  }
  return mutedOffices.value[selectedOfficeIndex.value].office.name;
});

async function fetchMutedOffices() {
  const { results } = await accountService.fetchMutedOffices({
    isMuted: true,
    ordering: "-updated_at"
  });
  mutedOffices.value = results;
}

async function updateMutedOffice({
  index,
  reason
}: {
  index: number;
  reason?: string;
}) {
  loader.show();
  try {
    const mutedOffice = mutedOffices.value[index];
    const isMutedBeforeUpdate = mutedOffice.isMuted; // 変更前にミュート状態ならtrue
    await accountService.updateMutedOffice({
      officeId: mutedOffice.office.id,
      isMuted: !mutedOffice.isMuted,
      reason
    });
    const newMutedOffices = mutedOffices.value.slice();
    newMutedOffices.splice(index, 1, {
      ...mutedOffice,
      isMuted: !isMutedBeforeUpdate
    });
    mutedOffices.value = newMutedOffices;
    toast.toast(
      isMutedBeforeUpdate
        ? "事業所のミュートを解除しました"
        : "事業所をミュートしました",
      {
        toastClass: ["toast-custom", "toast-custom-thin-header-page"],
        toaster: "b-toaster-top-full",
        autoHideDelay: 2000
      }
    );

    // ミュート解除時にキャッシュが残っている場合、
    // ミュートを解除したのに案件一覧に案件が表示されなくなってしまうため、キャッシュをクリアする
    if (isMutedBeforeUpdate) {
      orderStore.clearLimitedOrderListCache();
      orderStore.clearLimitedOrderListCountCache();
    }
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
}

async function onClickMuteButton(index: number) {
  const mutedOffice = mutedOffices.value[index];
  if (mutedOffice.isMuted) {
    await updateMutedOffice({ index });
    return;
  }
  selectedOfficeIndex.value = index;
  isShownMutedOfficeModal.value = true;
}

async function onSubmitMuteOfficeModal({ reason }: { reason: string }) {
  await updateMutedOffice({ index: selectedOfficeIndex.value!, reason });
  onCloseMuteOfficeModal();
}

function onCloseMuteOfficeModal() {
  isShownMutedOfficeModal.value = false;
  selectedOfficeIndex.value = null;
}

function onClickBack() {
  router.back();
}
</script>
<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      <div>ミュート</div>
    </Header>
    <template v-if="initialized">
      <div class="muted-office-list p-3">
        <div v-for="(mutedOffice, index) in mutedOffices" :key="mutedOffice.id">
          <OfficeCard :office="mutedOffice.office">
            <OfficeMuteButton
              :isMuted="mutedOffice.isMuted"
              @click="onClickMuteButton(index)"
            />
          </OfficeCard>
        </div>
        <p
          v-if="mutedOffices.length === 0"
          class="p-3 bg-white text-size-14 text-center"
        >
          ミュートリストに登録された事業所はありません
        </p>
      </div>
    </template>
    <AppFooter />
    <OfficeMuteModal
      :isShown="isShownMutedOfficeModal"
      :officeName="selectedOfficeName"
      @submit="onSubmitMuteOfficeModal"
      @cancel="onCloseMuteOfficeModal"
    />
  </div>
</template>
<style lang="scss" scoped>
.muted-office-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
